import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import ProfileCardContent from '@/components/profile/ProfileCardContent';
import ProfileCardFooter from '@/components/profile/ProfileCardFooter';
import ProfileCardHeader from '@/components/profile/ProfileCardHeader';
import { EscortProfile } from '@/types';
import { useLanguage } from '@/context/language-context';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/auth';
import { useToast } from '@/hooks/use-toast';
import { useConversations } from '@/hooks/useConversations';

interface ProfileCardProps {
  profile: EscortProfile;
  isFavorite: boolean;
  onToggleFavorite: (profileId: string) => void;
  profileImages: Record<string, string>;
  isGeneratingImage?: boolean;
  onGenerateImage?: () => Promise<string | void>;
  storageAvailable?: boolean | null;
}

const ProfileCard: React.FC<ProfileCardProps> = ({
  profile,
  isFavorite,
  onToggleFavorite,
  profileImages,
  isGeneratingImage,
  onGenerateImage,
  storageAvailable
}) => {
  const { t, currentLanguage, getUrlWithLanguage } = useLanguage();
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { startConversation } = useConversations();
  
  console.log('Rendering ProfileCard for:', profile.id, profile.displayName);
  
  const handleViewProfile = () => {
    console.log(`Navigating to profile: ${getUrlWithLanguage(`/profile/${profile.id}`)}`);
    navigate(getUrlWithLanguage(`/profile/${profile.id}`));
  };
  
  const handleSendMessage = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      toast({
        title: t('common.login_required'),
        description: t('common.login_to_message'),
        variant: 'default',
      });
      navigate(getUrlWithLanguage('/login'));
      return;
    }
    
    console.log('Send message clicked for:', profile.id);
    
    try {
      const conversation = await startConversation(profile.id);
      if (conversation) {
        console.log('[ProfileCard] Conversation started/found, navigating to:', `/messages/${conversation.id}`);
        navigate(getUrlWithLanguage(`/messages/${conversation.id}`));
      } else {
        console.error('Failed to start conversation from ProfileCard');
      }
    } catch (error) {
      console.error('Error in handleSendMessage calling startConversation:', error);
      toast({
        title: t('errors.error'),
        description: t('errors.generic_error'),
        variant: 'destructive',
      });
    }
  };

  // Create a specific function to handle click on the favorite button
  const handleToggleFavorite = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      toast({
        title: t('common.login_required'),
        description: t('common.login_to_favorite'),
        variant: 'default',
      });
      navigate(getUrlWithLanguage('/login'));
      return;
    }
    
    onToggleFavorite(profile.id);
  };

  // Priorité d'utilisation des images:
  // 1. L'URL pré-chargée dans profileImages (si elle existe)
  // 2. profile.photos.main (si existant)
  // 3. Chaîne vide (pour laisser ProfileCardHeader gérer le placeholder)
  const imageUrl = (profileImages && profileImages[profile.id]) || profile?.photos?.main || '';
  
  console.log('Profile card image URL:', imageUrl, 'for profile:', profile.id);
  
  return (
    <Card 
      className="overflow-hidden flex flex-col h-full hover:shadow-lg transition-all duration-300 cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleViewProfile}
    >
      <ProfileCardHeader 
        imageUrl={imageUrl}
        displayName={profile.displayName}
        isVerified={profile.isVerified}
        isOnline={profile.isOnline}
        isTouring={profile.isTouring}
        isFavorite={isFavorite}
        isHovered={isHovered}
        isLoadingImage={isGeneratingImage}
        handleToggleFavorite={handleToggleFavorite}
        storageAvailable={storageAvailable}
        profileId={profile.id}
      />
      
      <div className="flex-1 p-4 flex flex-col justify-between">
        <ProfileCardContent profile={profile} canSeeRates={true} />
        
        <ProfileCardFooter 
          handleViewProfile={handleViewProfile} 
          handleSendMessage={handleSendMessage} 
          canMessage={true} 
        />
      </div>
    </Card>
  );
};

export default ProfileCard;
